import { useState, useEffect } from "react";
import { useAuth, API, authFetch } from "@/App";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  MapPin, ShoppingCart, Target, Gift, Receipt, ClipboardList, Store,
  Bell, FileText, Route, Save, Package, DollarSign, Lock, Building2,
  ChevronDown, ChevronUp, BookOpen, Zap, Smartphone, CheckCircle2, RefreshCw,
  Users, ExternalLink, FlaskConical, Eraser, Sparkles, AlertTriangle,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BigToggle } from "@/components/admin/BigToggle";
import { MultiSelectChips } from "@/components/admin/MultiSelectChips";
import { WeekdayPicker } from "@/components/admin/WeekdayPicker";
import { ApprovalChainEditor } from "@/components/admin/ApprovalChainEditor";
import { ApprovalFlowPreview } from "@/components/admin/ApprovalFlowPreview";
import { PerEmployeeTADAEditor } from "@/components/admin/PerEmployeeTADAEditor";
import { Link } from "react-router-dom";

// ── Config Section Component ──
const ConfigSection = ({ title, icon: Icon, color, syncStatus, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <Card className={`dark:bg-slate-800 overflow-hidden ${open ? "ring-1 ring-slate-300 dark:ring-slate-600" : ""}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50 dark:hover:bg-slate-700/50 transition" data-testid={`config-section-${title.toLowerCase().replace(/[\s&\/]+/g,"-")}`}>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}><Icon className="w-4 h-4 text-white" /></div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-slate-900 dark:text-white">{title}</p>
        </div>
        {syncStatus === "synced" && (
          <span className="hidden sm:inline-flex items-center gap-1 text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
            <CheckCircle2 className="w-3 h-3" /> Synced to mobile
          </span>
        )}
        {syncStatus === "saving" && (
          <span className="hidden sm:inline-flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400 font-medium">
            <RefreshCw className="w-3 h-3 animate-spin" /> Syncing…
          </span>
        )}
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="px-4 pb-4 border-t dark:border-slate-700 pt-3">{children}</div>}
    </Card>
  );
};

// ── Toggle Row (uses BigToggle) ──
const Toggle = ({ label, desc, value, onChange, testId }) => (
  <div className="flex items-center justify-between gap-3 py-2">
    <div className="min-w-0">
      <p className="text-xs font-medium text-slate-700 dark:text-slate-300">{label}</p>
      {desc && <p className="text-[10px] text-slate-400">{desc}</p>}
    </div>
    <BigToggle value={value} onChange={onChange} testId={testId} />
  </div>
);

const NumRow = ({ label, desc, value, onChange, suffix, testId, wide }) => (
  <div className="flex items-center justify-between py-1.5 gap-3">
    <div className="min-w-0"><p className="text-xs font-medium text-slate-700 dark:text-slate-300">{label}</p>{desc && <p className="text-[10px] text-slate-400">{desc}</p>}</div>
    <div className="flex items-center gap-1 shrink-0">
      <Input
        type="number"
        value={value ?? 0}
        onChange={e => onChange(parseFloat(e.target.value) || 0)}
        className={`${wide ? "w-32" : "w-20"} h-7 text-xs text-right`}
        data-testid={testId}
      />
      {suffix && <span className="text-[10px] text-slate-400">{suffix}</span>}
    </div>
  </div>
);

const SelectRow = ({ label, value, onChange, options }) => (
  <div className="flex items-center justify-between py-1.5">
    <p className="text-xs font-medium text-slate-700 dark:text-slate-300">{label}</p>
    <Select value={String(value)} onValueChange={onChange}><SelectTrigger className="w-32 h-7 text-xs"><SelectValue /></SelectTrigger><SelectContent>{options.map(o => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}</SelectContent></Select>
  </div>
);

const TextRow = ({ label, value, onChange, placeholder }) => (
  <div className="flex items-center justify-between py-1.5">
    <p className="text-xs font-medium text-slate-700 dark:text-slate-300">{label}</p>
    <Input value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-48 h-7 text-xs" />
  </div>
);

// Plain comma-separated list (kept for KYC docs, allowed formats etc.)
const ListRow = ({ label, value, onChange }) => (
  <div className="py-1.5">
    <p className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">{label}</p>
    <Input value={Array.isArray(value) ? value.join(", ") : value || ""} onChange={e => onChange(e.target.value.split(",").map(s => s.trim()).filter(Boolean))} className="h-7 text-xs" placeholder="Option 1, Option 2, ..." />
  </div>
);

const Divider = ({ label }) => <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mt-3 mb-1 border-b pb-1 dark:border-slate-700">{label}</p>;

// ── Module Config Hook ──
function useModuleConfig(moduleId) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [synced, setSynced] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await authFetch(`${API}/admin/module-config/${moduleId}`);
      const d = await r.json();
      if (d.success) setCfg(d.data);
      setLoading(false);
    })();
  }, [moduleId]);

  const save = async () => {
    setSaving(true);
    setSynced(false);
    try {
      const r = await authFetch(`${API}/admin/module-config/${moduleId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(cfg),
      });
      const d = await r.json();
      if (d.success) {
        setSynced(true);
        toast.success(`${moduleId} settings saved & synced to mobile`, {
          description: "Connected mobile devices will refresh within 5 seconds.",
        });
        setTimeout(() => setSynced(false), 5000);
      } else {
        toast.error(d.detail || "Failed to save");
      }
    } catch {
      toast.error("Network error - settings not saved");
    } finally {
      setSaving(false);
    }
  };

  const set = (key, val) => setCfg(prev => ({ ...prev, [key]: val }));
  return { cfg, loading, saving, synced, save, set };
}

const SaveBtn = ({ saving, synced, onClick }) => (
  <div className="flex items-center gap-3 mt-3">
    <Button size="sm" onClick={onClick} disabled={saving} className="bg-[#8B1A1A] hover:bg-[#6B1515] text-white" data-testid="save-config">
      <Save className="w-3 h-3 mr-1" />{saving ? "Saving…" : "Save & Sync to Mobile"}
    </Button>
    {synced && (
      <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 font-medium" data-testid="synced-indicator">
        <CheckCircle2 className="w-3.5 h-3.5" /> Synced to mobile
      </span>
    )}
  </div>
);

// ═══════════════════ MAIN PAGE ═══════════════════
const AdminPage = () => {
  const { user } = useAuth();
  if (user?.role !== "SUPER_ADMIN") return <Navigate to="/dashboard" replace />;

  const forceSyncMobile = async () => {
    try {
      const res = await authFetch(`${API}/v1/settings/sync`, { method: "POST" });
      const d = await res.json();
      if (d.success) toast.success("Settings pushed to all mobile devices", { description: "All connected apps will refresh within 5 seconds." });
      else toast.error(d.detail || "Sync failed");
    } catch { toast.error("Network error - could not reach sync endpoint"); }
  };

  return (
    <div className="space-y-3" data-testid="admin-page">
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white font-['Sora']">Admin Panel</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">System configuration & settings. All changes sync to mobile app in real-time.</p>
        </div>
        <Button onClick={forceSyncMobile} className="bg-[#8B1A1A] hover:bg-[#6B1515] text-white shadow-sm" data-testid="force-sync-mobile-btn">
          <Smartphone className="w-4 h-4 mr-2" /> <Zap className="w-3.5 h-3.5 mr-1 opacity-70" /> Force Sync to Mobile
        </Button>
      </div>

      <ConfigSection title="GPS Attendance & Check-in" icon={MapPin} color="bg-blue-500"><AttendanceConfig /></ConfigSection>
      <ConfigSection title="Journey Plan & Visits" icon={Route} color="bg-indigo-500"><JourneyPlanConfig /></ConfigSection>
      <ConfigSection title="Dealer & Distributor Management" icon={Store} color="bg-emerald-500"><DealerConfig /></ConfigSection>
      <ConfigSection title="Product Catalogue" icon={Package} color="bg-violet-500"><ProductConfig /></ConfigSection>
      <ConfigSection title="Schemes Management" icon={Gift} color="bg-amber-500"><SchemeConfig /></ConfigSection>
      <ConfigSection title="Order Management" icon={ShoppingCart} color="bg-[#8B1A1A]"><OrderConfig /></ConfigSection>
      <ConfigSection title="Collections & Payments" icon={DollarSign} color="bg-teal-500"><CollectionConfig /></ConfigSection>
      <ConfigSection title="Expenses & TADA" icon={Receipt} color="bg-rose-500"><ExpenseConfig /></ConfigSection>
      <ConfigSection title="Task Management" icon={ClipboardList} color="bg-orange-500"><TaskConfig /></ConfigSection>
      <ConfigSection title="Targets & Performance" icon={Target} color="bg-purple-500"><TargetConfig /></ConfigSection>
      <ConfigSection title="Sales BEATs / Areas" icon={MapPin} color="bg-sky-500"><BeatConfig /></ConfigSection>
      <ConfigSection title="Notifications & Alerts" icon={Bell} color="bg-violet-600"><NotificationConfig /></ConfigSection>
      <ConfigSection title="Brochures" icon={BookOpen} color="bg-orange-600"><BrochureConfig /></ConfigSection>
      <ConfigSection title="Users & Permissions" icon={Users} color="bg-pink-600"><UsersPermissionsConfig /></ConfigSection>
      <ConfigSection title="Security, Roles & Access" icon={Lock} color="bg-slate-700"><SecurityConfig /></ConfigSection>
      <ConfigSection title="Company Profile" icon={Building2} color="bg-slate-500"><CompanyConfig /></ConfigSection>
      <ConfigSection title="Demo / Test Data" icon={FlaskConical} color="bg-violet-500"><DemoDataConfig /></ConfigSection>
    </div>
  );
};

// ═══════════ CONFIG PANELS ═══════════

const AttendanceConfig = () => {
  const { cfg, loading, saving, synced, save, set } = useModuleConfig("attendance");
  if (loading || !cfg) return <Loader />;
  return (<div>
    <Divider label="Punch Settings" />
    <Toggle label="Punch-In Required" desc="Employees must punch-in to start work" value={cfg.punch_in_required} onChange={v => set("punch_in_required", v)} testId="t-punch-in" />
    <Toggle label="Punch-Out Required" value={cfg.punch_out_required} onChange={v => set("punch_out_required", v)} />
    <Toggle label="Photo Required at Punch-In" desc="Selfie verification" value={cfg.photo_required_punch_in} onChange={v => set("photo_required_punch_in", v)} testId="t-photo-punch" />
    <Toggle label="Photo Required at Punch-Out" value={cfg.photo_required_punch_out} onChange={v => set("photo_required_punch_out", v)} />
    <Toggle label="GPS Required" desc="Location must be captured" value={cfg.gps_required} onChange={v => set("gps_required", v)} />
    <Divider label="Auto Punch" />
    <Toggle label="Auto Punch-In" desc="Automatically mark attendance at configured time" value={cfg.auto_punch_in_enabled} onChange={v => set("auto_punch_in_enabled", v)} testId="t-auto-punch-in" />
    <TextRow label="Auto Punch-In Time" value={cfg.auto_punch_in_time} onChange={v => set("auto_punch_in_time", v)} />
    <Toggle label="Auto Punch-Out" value={cfg.auto_punch_out_enabled} onChange={v => set("auto_punch_out_enabled", v)} />
    <TextRow label="Auto Punch-Out Time" value={cfg.auto_punch_out_time} onChange={v => set("auto_punch_out_time", v)} />
    <Divider label="GPS & Tracking" />
    <NumRow label="GPS Ping Interval" value={cfg.gps_ping_interval_seconds} onChange={v => set("gps_ping_interval_seconds", v)} suffix="sec" testId="n-gps-interval" />
    <NumRow label="Battery Alert Threshold" value={cfg.battery_alert_threshold} onChange={v => set("battery_alert_threshold", v)} suffix="%" />
    <NumRow label="Geofence Check-in Radius" value={cfg.geofence_checkin_radius_meters} onChange={v => set("geofence_checkin_radius_meters", v)} suffix="m" />
    <Divider label="Working Hours" />
    <TextRow label="Shift Start" value={cfg.shift_start} onChange={v => set("shift_start", v)} />
    <TextRow label="Shift End" value={cfg.shift_end} onChange={v => set("shift_end", v)} />
    <NumRow label="Late Check-in After" value={cfg.late_checkin_threshold_minutes} onChange={v => set("late_checkin_threshold_minutes", v)} suffix="min" />
    <NumRow label="Full Day Minimum Hours" value={cfg.min_hours_full_day} onChange={v => set("min_hours_full_day", v)} suffix="hrs" />
    <NumRow label="Half Day Minimum Hours" value={cfg.min_hours_half_day} onChange={v => set("min_hours_half_day", v)} suffix="hrs" />
    <WeekdayPicker label="Working Days" value={cfg.working_days} onChange={v => set("working_days", v)} testId="working-days" />
    <Toggle label="Allow Manual Attendance" desc="Admin can mark attendance manually" value={cfg.allow_manual_attendance} onChange={v => set("allow_manual_attendance", v)} />
    <SaveBtn saving={saving} synced={synced} onClick={save} />
  </div>);
};

const JourneyPlanConfig = () => {
  const { cfg, loading, saving, synced, save, set } = useModuleConfig("journey_plan");
  if (loading || !cfg) return <Loader />;
  return (<div>
    <Toggle label="Journey Plan Enabled" value={cfg.enabled} onChange={v => set("enabled", v)} />
    <Toggle label="Geofence Validation" desc="Flag visits outside dealer GPS location" value={cfg.geofence_validation} onChange={v => set("geofence_validation", v)} />
    <NumRow label="Geofence Radius" value={cfg.geofence_radius_meters} onChange={v => set("geofence_radius_meters", v)} suffix="m" />
    <Toggle label="Allow Skip Visit" value={cfg.allow_skip_visit} onChange={v => set("allow_skip_visit", v)} />
    <Toggle label="Skip Reason Required" value={cfg.skip_reason_required} onChange={v => set("skip_reason_required", v)} />
    <NumRow label="Min Visit Duration" value={cfg.min_visit_duration_minutes} onChange={v => set("min_visit_duration_minutes", v)} suffix="min" />
    <NumRow label="Max Visits Per Day" value={cfg.max_visits_per_day} onChange={v => set("max_visits_per_day", v)} />
    <Toggle label="Auto Check-in on Geofence" desc="Auto-checkin when entering dealer radius" value={cfg.auto_checkin_on_geofence} onChange={v => set("auto_checkin_on_geofence", v)} />
    <Toggle label="Client Meeting Tracking" value={cfg.client_meeting_tracking} onChange={v => set("client_meeting_tracking", v)} />
    <SaveBtn saving={saving} synced={synced} onClick={save} />
  </div>);
};

const DealerConfig = () => {
  const { cfg, loading, saving, synced, save, set } = useModuleConfig("dealers");
  if (loading || !cfg) return <Loader />;
  return (<div>
    <Divider label="Onboarding" />
    <Toggle label="Onboarding Approval Required" desc="New dealers need admin approval" value={cfg.onboarding_approval_required} onChange={v => set("onboarding_approval_required", v)} testId="t-dealer-approval" />
    <Toggle label="Auto-Approve Dealers" value={cfg.auto_approve_dealers} onChange={v => set("auto_approve_dealers", v)} />
    <Toggle label="Require GPS on Registration" value={cfg.require_gps_on_registration} onChange={v => set("require_gps_on_registration", v)} />
    <Toggle label="Require Shop Photo" value={cfg.require_shop_photo} onChange={v => set("require_shop_photo", v)} />
    <Divider label="Credit & Finance" />
    <NumRow label="Default Credit Limit (Rs)" value={cfg.default_credit_limit} onChange={v => set("default_credit_limit", v)} testId="n-default-credit" wide />
    <Toggle label="Enforce Credit Limit" desc="Block orders above limit" value={cfg.credit_limit_enforcement} onChange={v => set("credit_limit_enforcement", v)} />
    <NumRow label="Credit Alert at (%)" value={cfg.credit_alert_at_percent} onChange={v => set("credit_alert_at_percent", v)} suffix="%" />
    <Toggle label="Show Outstanding to Salesman" value={cfg.show_outstanding_to_salesman} onChange={v => set("show_outstanding_to_salesman", v)} />
    <Divider label="KYC Documents" />
    <Toggle label="KYC Documents Required" value={cfg.kyc_documents_required} onChange={v => set("kyc_documents_required", v)} />
    <MultiSelectChips
      label="KYC Document Types"
      value={cfg.kyc_document_types}
      onChange={v => set("kyc_document_types", v)}
      presets={["PAN", "GSTIN", "Aadhar", "Bank Statement", "Cancelled Cheque", "Trade License", "MSME Certificate", "Shop Photo"]}
      placeholder="Add new document type"
      testId="kyc-document-types"
    />
    <Divider label="Catalogue & Pricing" />
    <Toggle label="Dynamic Catalogue (per dealer)" desc="Hide/show products per dealer" value={cfg.dynamic_catalogue_enabled} onChange={v => set("dynamic_catalogue_enabled", v)} />
    <Toggle label="Dynamic Pricing (per dealer)" desc="Dealer-specific price lists" value={cfg.dynamic_pricing_enabled} onChange={v => set("dynamic_pricing_enabled", v)} />
    <Divider label="Loyalty" />
    <Toggle label="Loyalty Points Enabled" value={cfg.loyalty_points_enabled} onChange={v => set("loyalty_points_enabled", v)} />
    <NumRow label="Points per Rs 100" value={cfg.loyalty_points_per_100_rupees} onChange={v => set("loyalty_points_per_100_rupees", v)} />
    <Divider label="Categories & Types" />
    <MultiSelectChips
      label="Category Options"
      value={cfg.category_options}
      onChange={v => set("category_options", v)}
      presets={["A", "B", "C", "D", "PLATINUM", "GOLD", "SILVER", "BRONZE"]}
      placeholder="Add new category"
      testId="dealer-category-options"
    />
    <MultiSelectChips
      label="Party Types"
      value={cfg.party_types}
      onChange={v => set("party_types", v)}
      presets={["DEALER", "DISTRIBUTOR", "RETAILER", "CONTRACTOR", "BUILDER", "ARCHITECT", "INDUSTRIAL", "GOVERNMENT"]}
      placeholder="Add new party type"
      testId="dealer-party-types"
    />
    <Divider label="Dealer Form Fields" />
    <Toggle label="Allow Salesman to Edit Dealer" value={cfg.allow_salesman_edit_dealer} onChange={v => set("allow_salesman_edit_dealer", v)} />
    <p className="text-[10px] text-slate-400 mb-1 mt-2">Pick which fields appear in the Add/Edit Dealer dialog. Required fields cannot be hidden.</p>
    <MultiSelectChips
      label="Visible Form Fields"
      value={cfg.dealer_form_fields}
      onChange={v => set("dealer_form_fields", v)}
      presets={[
        "firm_name", "contact_person", "mobile_number", "alternate_mobile", "email",
        "party_type", "gstin", "pan", "address", "district", "taluka", "state",
        "category", "credit_limit", "bank_name", "bank_account_number", "ifsc_code",
        "delivery_addresses", "site_address", "engineer_contact", "shop_photo", "gps_location",
      ]}
      placeholder="Add a custom field key"
      testId="dealer-form-fields"
    />
    <MultiSelectChips
      label="Required Fields"
      value={cfg.dealer_required_fields}
      onChange={v => set("dealer_required_fields", v)}
      presets={["firm_name", "contact_person", "mobile_number", "address", "gstin", "pan", "category"]}
      placeholder="Mark a field as required"
      testId="dealer-required-fields"
    />
    <SaveBtn saving={saving} synced={synced} onClick={save} />
  </div>);
};

const ProductConfig = () => {
  const { cfg, loading, saving, synced, save, set } = useModuleConfig("products");
  if (loading || !cfg) return <Loader />;
  return (<div>
    <Toggle label="Allow Product Images" value={cfg.allow_product_images} onChange={v => set("allow_product_images", v)} />
    <Toggle label="Allow Product Videos" value={cfg.allow_product_videos} onChange={v => set("allow_product_videos", v)} />
    <Toggle label="Require SKU Code" value={cfg.require_sku_code} onChange={v => set("require_sku_code", v)} />
    <Toggle label="Require HSN Code" value={cfg.require_hsn_code} onChange={v => set("require_hsn_code", v)} />
    <NumRow label="Default GST Rate" value={cfg.default_gst_rate} onChange={v => set("default_gst_rate", v)} suffix="%" />
    <MultiSelectChips
      label="GST Rate Options (%)"
      value={cfg.gst_rate_options}
      onChange={v => set("gst_rate_options", v)}
      presets={[0, 5, 12, 18, 28]}
      kind="number"
      placeholder="Add custom GST % (e.g. 3)"
      testId="gst-rate-options"
    />
    <Toggle label="Dealer-Specific Pricing" value={cfg.enable_dealer_specific_pricing} onChange={v => set("enable_dealer_specific_pricing", v)} />
    <Toggle label="Group Pricing" value={cfg.enable_group_pricing} onChange={v => set("enable_group_pricing", v)} />
    <Toggle label="Show MRP to Salesmen" value={cfg.show_mrp_to_salesmen} onChange={v => set("show_mrp_to_salesmen", v)} />
    <ListRow label="Product Categories" value={cfg.categories} onChange={v => set("categories", v)} />
    <MultiSelectChips
      label="Units of Measurement"
      value={cfg.units_of_measurement}
      onChange={v => set("units_of_measurement", v)}
      presets={["MT", "BAGS", "KG", "PCS", "CBM", "LTR", "TON"]}
      placeholder="Add new unit (e.g. CBM)"
      testId="units-of-measurement"
    />
    <SaveBtn saving={saving} synced={synced} onClick={save} />
  </div>);
};

const SchemeConfig = () => {
  const { cfg, loading, saving, synced, save, set } = useModuleConfig("schemes");
  if (loading || !cfg) return <Loader />;
  return (<div>
    <Toggle label="Schemes Enabled" value={cfg.enabled} onChange={v => set("enabled", v)} />
    <Toggle label="Auto-Apply on Order" desc="Automatically apply matching schemes" value={cfg.auto_apply_on_order} onChange={v => set("auto_apply_on_order", v)} />
    <NumRow label="Max Schemes per Order" value={cfg.max_schemes_per_order} onChange={v => set("max_schemes_per_order", v)} />
    <Toggle label="Allow Scheme Stacking" desc="Multiple schemes on same order" value={cfg.allow_scheme_stacking} onChange={v => set("allow_scheme_stacking", v)} />
    <Toggle label="Notify Dealers on Launch" value={cfg.notify_dealers_on_launch} onChange={v => set("notify_dealers_on_launch", v)} />
    <Toggle label="Notify Salesmen on Launch" value={cfg.notify_salesmen_on_launch} onChange={v => set("notify_salesmen_on_launch", v)} />
    <Toggle label="Show Scheme on Product Page" value={cfg.show_scheme_on_product_page} onChange={v => set("show_scheme_on_product_page", v)} />
    {/* Scheme Types removed per requirement */}
    <SaveBtn saving={saving} synced={synced} onClick={save} />
  </div>);
};

const OrderConfig = () => {
  const { cfg, loading, saving, synced, save, set } = useModuleConfig("orders");
  if (loading || !cfg) return <Loader />;
  return (<div>
    <Divider label="Approval" />
    <Toggle label="Require Admin Approval" value={cfg.require_admin_approval} onChange={v => set("require_admin_approval", v)} testId="t-order-approval" />
    <NumRow label="Auto-Approve Below (Rs)" value={cfg.auto_approve_below_amount} onChange={v => set("auto_approve_below_amount", v)} suffix="Rs" testId="n-auto-approve" />
    <NumRow label="Min Order Amount" value={cfg.min_order_amount} onChange={v => set("min_order_amount", v)} suffix="Rs" />
    <Divider label="Approval Workflow" />
    <ApprovalChainEditor
      label="Order Approval Flow"
      description="Define the multi-step approval flow. Each step routes the order to the chosen role for approval before it moves forward."
      value={cfg.approval_chain || []}
      onChange={v => set("approval_chain", v)}
      testId="order-approval-chain"
    />
    <ApprovalFlowPreview chain={cfg.approval_chain || []} entity="Order" title="Order Approval Workflow" />
    <Divider label="Routing" />
    <Toggle label="Salesman Selects Unit" desc="Allow salesman to choose Unit 1/2" value={cfg.allow_salesman_unit_selection} onChange={v => set("allow_salesman_unit_selection", v)} />
    <SelectRow label="Default Unit" value={cfg.default_unit} onChange={v => set("default_unit", v)} options={[{value:"unit_1",label:"Unit 1"},{value:"unit_2",label:"Unit 2"}]} />
    <Divider label="Edit & Cancel" />
    <NumRow label="Edit Window" value={cfg.edit_window_hours} onChange={v => set("edit_window_hours", v)} suffix="hrs" />
    <NumRow label="Cancel Window" value={cfg.cancel_window_hours} onChange={v => set("cancel_window_hours", v)} suffix="hrs" />
    <Divider label="Requirements" />
    <Toggle label="Require Delivery Address" value={cfg.require_delivery_address} onChange={v => set("require_delivery_address", v)} />
    <Toggle label="Require PO Number" value={cfg.require_po_number} onChange={v => set("require_po_number", v)} />
    <NumRow label="Default GST Rate" value={cfg.default_gst_rate} onChange={v => set("default_gst_rate", v)} suffix="%" />
    <Toggle label="Enable Secondary Orders" value={cfg.enable_secondary_orders} onChange={v => set("enable_secondary_orders", v)} />
    <Divider label="Notifications" />
    <Toggle label="Notify on Status Change" value={cfg.notify_on_status_change} onChange={v => set("notify_on_status_change", v)} />
    <Toggle label="Notify Dealer on Dispatch" value={cfg.notify_dealer_on_dispatch} onChange={v => set("notify_dealer_on_dispatch", v)} />
    <MultiSelectChips
      label="Order Statuses"
      value={cfg.statuses}
      onChange={v => set("statuses", v)}
      presets={["PLACED", "APPROVED", "ON_HOLD", "DISPATCHED", "IN_TRANSIT", "DELIVERED", "CANCELLED", "RETURNED"]}
      placeholder="Add new status (e.g. ON_REVIEW)"
      testId="order-statuses"
    />
    <SaveBtn saving={saving} synced={synced} onClick={save} />
  </div>);
};

const CollectionConfig = () => {
  const { cfg, loading, saving, synced, save, set } = useModuleConfig("collections");
  if (loading || !cfg) return <Loader />;
  return (<div>
    <Toggle label="Auto-Reconcile" value={cfg.auto_reconcile} onChange={v => set("auto_reconcile", v)} />
    <Toggle label="Require Receipt Photo" value={cfg.require_receipt_photo} onChange={v => set("require_receipt_photo", v)} />
    <Toggle label="Allow Partial Payment" value={cfg.allow_partial_payment} onChange={v => set("allow_partial_payment", v)} />
    <Toggle label="Show Outstanding on Mobile" value={cfg.show_outstanding_on_mobile} onChange={v => set("show_outstanding_on_mobile", v)} />
    <MultiSelectChips
      label="Payment Modes"
      value={cfg.payment_modes}
      onChange={v => set("payment_modes", v)}
      presets={["CASH", "CHEQUE", "UPI", "NEFT", "RTGS", "DD", "IMPS", "CARD", "BANK_TRANSFER"]}
      placeholder="Add new payment mode"
      testId="payment-modes"
    />
    <SaveBtn saving={saving} synced={synced} onClick={save} />
  </div>);
};

const ExpenseConfig = () => {
  const { cfg, loading, saving, synced, save, set } = useModuleConfig("expenses");
  if (loading || !cfg) return <Loader />;
  return (<div>
    <Divider label="TADA" />
    <Toggle label="Auto Travel Expense from GPS" value={cfg.auto_travel_expense_from_gps} onChange={v => set("auto_travel_expense_from_gps", v)} />
    <Toggle
      label="Auto-Detect Odometer from Photo"
      desc="Use AI/OCR to read odometer reading from uploaded photo"
      value={cfg.auto_detect_odometer_from_photo}
      onChange={v => set("auto_detect_odometer_from_photo", v)}
      testId="t-auto-odometer"
    />
    <NumRow label="TADA Rate per KM" value={cfg.tada_rate_per_km} onChange={v => set("tada_rate_per_km", v)} suffix="Rs/km" testId="n-tada-rate" />
    <NumRow label="Daily Allowance" value={cfg.daily_allowance} onChange={v => set("daily_allowance", v)} suffix="Rs" />
    <NumRow label="Food Allowance" value={cfg.food_allowance} onChange={v => set("food_allowance", v)} suffix="Rs" />
    <NumRow label="Min Hours Full TADA" value={cfg.min_hours_full_tada} onChange={v => set("min_hours_full_tada", v)} suffix="hrs" />
    <NumRow label="Min Hours Half TADA" value={cfg.min_hours_half_tada} onChange={v => set("min_hours_half_tada", v)} suffix="hrs" />
    <Divider label="Expense Rules" />
    <Toggle label="Approval Required" value={cfg.approval_required} onChange={v => set("approval_required", v)} />
    <NumRow label="Receipt Required Above" value={cfg.require_receipt_above_amount} onChange={v => set("require_receipt_above_amount", v)} suffix="Rs" />
    <NumRow label="Max Expense per Day" desc="0 = unlimited" value={cfg.max_expense_per_day} onChange={v => set("max_expense_per_day", v)} suffix="Rs" />
    <MultiSelectChips
      label="Expense Categories"
      value={cfg.expense_categories}
      onChange={v => set("expense_categories", v)}
      presets={["Travel", "Food", "Accommodation", "Phone", "Internet", "Stationery", "Client Entertainment", "Fuel", "Toll", "Parking", "Miscellaneous"]}
      placeholder="Add new category"
      testId="expense-categories"
    />
    <Divider label="Approval Workflow" />
    <ApprovalChainEditor
      label="Expense Approval Flow"
      description="Multi-step approval routing for expense claims (e.g., Manager → Finance → Admin)."
      value={cfg.approval_chain || []}
      onChange={v => set("approval_chain", v)}
      testId="expense-approval-chain"
    />
    <ApprovalFlowPreview chain={cfg.approval_chain || []} entity="Expense" title="Expense Approval Workflow" />
    <Divider label="Per-Employee Allowances" />
    <PerEmployeeTADAEditor defaults={cfg} />
    <SaveBtn saving={saving} synced={synced} onClick={save} />
  </div>);
};

const TaskConfig = () => {
  const { cfg, loading, saving, synced, save, set } = useModuleConfig("tasks");
  if (loading || !cfg) return <Loader />;
  return (<div>
    <Toggle label="Tasks Enabled" value={cfg.enabled} onChange={v => set("enabled", v)} />
    <Toggle label="Allow Self Assignment" value={cfg.allow_self_assignment} onChange={v => set("allow_self_assignment", v)} />
    <Toggle label="Require Completion Notes" value={cfg.require_completion_notes} onChange={v => set("require_completion_notes", v)} />
    <Toggle label="Require Completion Photo" value={cfg.require_completion_photo} onChange={v => set("require_completion_photo", v)} />
    <Divider label="Auto Close" />
    <Toggle
      label="Auto-Close Stale Tasks"
      desc="Automatically close tasks that have not been updated within the days configured below"
      value={cfg.auto_close_enabled !== false}
      onChange={v => set("auto_close_enabled", v)}
      testId="t-auto-close"
    />
    {cfg.auto_close_enabled !== false && (
      <NumRow label="Auto-Close After" value={cfg.auto_close_after_days} onChange={v => set("auto_close_after_days", v)} suffix="days" />
    )}
    <Divider label="Lists" />
    <MultiSelectChips
      label="Task Types"
      value={cfg.task_types}
      onChange={v => set("task_types", v)}
      presets={["VISIT", "CALL", "FOLLOW_UP", "COLLECTION", "DELIVERY", "SURVEY", "DEMO", "TRAINING", "OTHER"]}
      placeholder="Add new task type"
      testId="task-types"
    />
    <MultiSelectChips
      label="Priority Levels"
      value={cfg.priority_levels}
      onChange={v => set("priority_levels", v)}
      presets={["LOW", "MEDIUM", "HIGH", "URGENT", "CRITICAL"]}
      placeholder="Add new priority level"
      testId="priority-levels"
    />
    <SaveBtn saving={saving} synced={synced} onClick={save} />
  </div>);
};

const TargetConfig = () => {
  const { cfg, loading, saving, synced, save, set } = useModuleConfig("targets");
  if (loading || !cfg) return <Loader />;
  return (<div>
    <Divider label="Display & Visibility" />
    <Toggle label="Show Leaderboard" value={cfg.show_leaderboard} onChange={v => set("show_leaderboard", v)} />
    <Toggle label="Show Team Targets" value={cfg.show_team_targets} onChange={v => set("show_team_targets", v)} />
    <Toggle label="Show Achievement % on Mobile" value={cfg.show_achievement_percent_mobile !== false} onChange={v => set("show_achievement_percent_mobile", v)} />
    <Toggle label="Show Region-Wise Targets" desc="Separate Gujarat / Rajasthan boards" value={cfg.show_region_wise_targets} onChange={v => set("show_region_wise_targets", v)} />
    <Divider label="Calculation" />
    <Toggle label="Auto-Recalculate on Order" desc="Update achievement instantly when an order is placed/approved" value={cfg.auto_recalculate_on_order !== false} onChange={v => set("auto_recalculate_on_order", v)} />
    <Toggle label="Allow Carry-Forward" desc="Unused target rolls over to next period" value={cfg.allow_carry_forward} onChange={v => set("allow_carry_forward", v)} />
    <Toggle label="Pro-Rate for Joiners" desc="Adjust target proportionally for partial-period joiners" value={cfg.pro_rate_for_new_joiners} onChange={v => set("pro_rate_for_new_joiners", v)} />
    <Divider label="Achievement Tiers (%)" />
    <NumRow label="Bronze Threshold" value={cfg.tier_bronze_percent ?? 50} onChange={v => set("tier_bronze_percent", v)} suffix="%" />
    <NumRow label="Silver Threshold" value={cfg.tier_silver_percent ?? 75} onChange={v => set("tier_silver_percent", v)} suffix="%" />
    <NumRow label="Gold Threshold" value={cfg.tier_gold_percent ?? 100} onChange={v => set("tier_gold_percent", v)} suffix="%" />
    <NumRow label="Platinum Threshold" value={cfg.tier_platinum_percent ?? 120} onChange={v => set("tier_platinum_percent", v)} suffix="%" />
    <Divider label="Notifications" />
    <Toggle label="Notify at 50% Achievement" value={cfg.notify_at_50_percent} onChange={v => set("notify_at_50_percent", v)} />
    <Toggle label="Notify at 75% Achievement" value={cfg.notify_at_75_percent} onChange={v => set("notify_at_75_percent", v)} />
    <Toggle label="Notify at 100% Achievement" value={cfg.notify_at_100_percent !== false} onChange={v => set("notify_at_100_percent", v)} />
    <Toggle label="Notify Manager on Shortfall" desc="Alert manager when team falls behind on target" value={cfg.notify_manager_on_shortfall} onChange={v => set("notify_manager_on_shortfall", v)} />
    <Divider label="Incentives" />
    <Toggle label="Incentives Enabled" value={cfg.incentive_enabled} onChange={v => set("incentive_enabled", v)} />
    <NumRow label="Points per 100% Achievement" value={cfg.points_per_100_percent} onChange={v => set("points_per_100_percent", v)} />
    <NumRow label="Bonus % above Target" desc="Cash incentive % for over-achievement" value={cfg.bonus_percent_above_target} onChange={v => set("bonus_percent_above_target", v)} suffix="%" />
    <Divider label="Types & Periods" />
    <MultiSelectChips
      label="Target Types"
      value={cfg.target_types}
      onChange={v => set("target_types", v)}
      presets={["SALES_VALUE", "SALES_VOLUME", "NEW_DEALERS", "VISITS", "COLLECTIONS", "ATTENDANCE", "ORDERS_COUNT", "ACTIVE_DEALERS"]}
      placeholder="Add new target type"
      testId="target-types"
    />
    <MultiSelectChips
      label="Periods"
      value={cfg.periods}
      onChange={v => set("periods", v)}
      presets={["DAILY", "WEEKLY", "FORTNIGHTLY", "MONTHLY", "QUARTERLY", "HALF_YEARLY", "YEARLY"]}
      placeholder="Add new period"
      testId="target-periods"
    />
    <SaveBtn saving={saving} synced={synced} onClick={save} />
  </div>);
};

const BeatConfig = () => {
  const { cfg, loading, saving, synced, save, set } = useModuleConfig("beats");
  if (loading || !cfg) return <Loader />;
  return (<div>
    <NumRow label="Max Dealers per BEAT" value={cfg.max_dealers_per_beat} onChange={v => set("max_dealers_per_beat", v)} />
    <Toggle label="Allow Multi-BEAT Assignment" desc="Salesman can be on multiple BEATs" value={cfg.allow_multi_beat_assignment} onChange={v => set("allow_multi_beat_assignment", v)} />
    <Toggle label="Show BEAT Map" value={cfg.show_beat_map} onChange={v => set("show_beat_map", v)} />
    <Toggle label="Require BEAT for Dealer" value={cfg.require_beat_for_dealer} onChange={v => set("require_beat_for_dealer", v)} />
    <Toggle label="Auto-Suggest BEAT" value={cfg.auto_suggest_beat} onChange={v => set("auto_suggest_beat", v)} />
    <SaveBtn saving={saving} synced={synced} onClick={save} />
  </div>);
};

const NotificationConfig = () => {
  const { cfg, loading, saving, synced, save, set } = useModuleConfig("notifications");
  if (loading || !cfg) return <Loader />;
  return (<div>
    <Divider label="Channels" />
    <Toggle label="Push Notifications" value={cfg.push_enabled} onChange={v => set("push_enabled", v)} />
    <Toggle label="Email Notifications" value={cfg.email_enabled} onChange={v => set("email_enabled", v)} />
    <Toggle label="SMS Notifications" value={cfg.sms_enabled} onChange={v => set("sms_enabled", v)} />
    <Toggle label="WhatsApp Notifications" value={cfg.whatsapp_enabled} onChange={v => set("whatsapp_enabled", v)} />
    <Divider label="Auto Triggers" />
    <Toggle label="Order Status Change" value={cfg.auto_notify_order_status} onChange={v => set("auto_notify_order_status", v)} />
    <Toggle label="Task Assignment" value={cfg.auto_notify_task_assignment} onChange={v => set("auto_notify_task_assignment", v)} />
    <Toggle label="Scheme Launch" value={cfg.auto_notify_scheme_launch} onChange={v => set("auto_notify_scheme_launch", v)} />
    <Toggle label="Target Achievement" value={cfg.auto_notify_target_achievement} onChange={v => set("auto_notify_target_achievement", v)} />
    <Toggle label="Late Check-in Alert" value={cfg.auto_notify_late_checkin} onChange={v => set("auto_notify_late_checkin", v)} />
    <Toggle label="Leave Approval / Rejection" desc="Notify the salesperson on the mobile app when their leave is approved/rejected/held" value={cfg.auto_notify_leave_status !== false} onChange={v => set("auto_notify_leave_status", v)} testId="t-notify-leave" />
    <Toggle label="Expense Approval / Rejection" desc="Notify the salesperson on the mobile app when their expense is approved/rejected/held" value={cfg.auto_notify_expense_status !== false} onChange={v => set("auto_notify_expense_status", v)} testId="t-notify-expense" />
    <Divider label="Daily Summary" />
    <Toggle label="Daily Summary Email" value={cfg.daily_summary_enabled} onChange={v => set("daily_summary_enabled", v)} />
    <TextRow label="Summary Time" value={cfg.daily_summary_time} onChange={v => set("daily_summary_time", v)} />
    <SaveBtn saving={saving} synced={synced} onClick={save} />
  </div>);
};

const BrochureConfig = () => {
  const { cfg, loading, saving, synced, save, set } = useModuleConfig("brochures");
  if (loading || !cfg) return <Loader />;
  return (<div>
    <Toggle label="Allow Salesman Download" value={cfg.allow_salesman_download} onChange={v => set("allow_salesman_download", v)} />
    <Toggle label="Allow Dealer Download" value={cfg.allow_dealer_download} onChange={v => set("allow_dealer_download", v)} />
    <NumRow label="Max File Size" value={cfg.max_file_size_mb} onChange={v => set("max_file_size_mb", v)} suffix="MB" />
    <ListRow label="Allowed Formats" value={cfg.allowed_formats} onChange={v => set("allowed_formats", v)} />
    {/* Brochure Categories removed per requirement */}
    <SaveBtn saving={saving} synced={synced} onClick={save} />
  </div>);
};

const UsersPermissionsConfig = () => (
  <div className="space-y-3" data-testid="users-permissions-section">
    <p className="text-xs text-slate-500 dark:text-slate-400">
      Add new admin / manager users, assign their role, region, mobile-app visibility, and granular module permissions.
      Each user can be restricted to specific pages (Sales, Operations, Factory sections).
    </p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Card className="p-3 dark:bg-slate-900 border-pink-200 dark:border-pink-900/50">
        <div className="flex items-start gap-2">
          <Users className="w-4 h-4 text-pink-600 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">Manage Users</p>
            <p className="text-[10px] text-slate-400 mb-2">
              Create users, set passwords, assign roles (Super Admin, Sales Admin, Accounts, Factory).
            </p>
            <Link to="/users" data-testid="open-user-management">
              <Button size="sm" className="h-7 text-[10px] bg-pink-600 hover:bg-pink-700 text-white">
                Open User Management <ExternalLink className="w-3 h-3 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </Card>
      <Card className="p-3 dark:bg-slate-900 border-indigo-200 dark:border-indigo-900/50">
        <div className="flex items-start gap-2">
          <Lock className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">Roles & Permissions</p>
            <p className="text-[10px] text-slate-400 mb-2">
              Configure roles, allowed modules, factory sections, and approval rights.
            </p>
            <Link to="/admin/config" data-testid="open-roles-config">
              <Button size="sm" variant="outline" className="h-7 text-[10px] border-indigo-300 text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/20">
                Open System Configuration <ExternalLink className="w-3 h-3 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </Card>
    </div>
    <Card className="p-3 dark:bg-slate-900 bg-slate-50">
      <div className="flex items-start gap-2 text-[10px] text-slate-500 dark:text-slate-400">
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
        <p>
          <span className="font-semibold text-slate-700 dark:text-slate-200">Tip:</span>{" "}
          Inside User Management → Edit User → set <em>Module Permissions</em> to control which Sales / Operations / Factory pages each user can see.
          For Factory Admins, pick the specific factory sections (Production, QC, Dispatch, etc.) they should access.
        </p>
      </div>
    </Card>
  </div>
);

const SecurityConfig = () => {
  const { cfg, loading, saving, synced, save, set } = useModuleConfig("security");
  if (loading || !cfg) return <Loader />;
  return (<div>
    <Divider label="Sessions" />
    <NumRow label="Session Timeout" value={cfg.session_timeout_hours} onChange={v => set("session_timeout_hours", v)} suffix="hrs" />
    <NumRow label="Max Active Sessions" value={cfg.max_active_sessions} onChange={v => set("max_active_sessions", v)} />
    <Toggle label="Force Logout Enabled" value={cfg.force_logout_enabled} onChange={v => set("force_logout_enabled", v)} />
    <Toggle label="Two-Factor Authentication" value={cfg.two_factor_enabled} onChange={v => set("two_factor_enabled", v)} />
    <Divider label="Account Lock" />
    <NumRow label="Lock After Failed Attempts" desc="Account is locked after this many consecutive failed logins" value={cfg.login_attempts_before_lock ?? 3} onChange={v => set("login_attempts_before_lock", v)} testId="n-lock-attempts" />
    <SaveBtn saving={saving} synced={synced} onClick={save} />
  </div>);
};

const CompanyConfig = () => {
  const { cfg, loading, saving, synced, save, set } = useModuleConfig("company");
  if (loading || !cfg) return <Loader />;
  return (<div>
    <TextRow label="Company Name" value={cfg.company_name} onChange={v => set("company_name", v)} />
    <TextRow label="Short Name" value={cfg.company_short} onChange={v => set("company_short", v)} />
    <TextRow label="GST Number" value={cfg.gst_number} onChange={v => set("gst_number", v)} />
    <TextRow label="PAN Number" value={cfg.pan_number} onChange={v => set("pan_number", v)} />
    <TextRow label="Contact Email" value={cfg.contact_email} onChange={v => set("contact_email", v)} />
    <TextRow label="Contact Phone" value={cfg.contact_phone} onChange={v => set("contact_phone", v)} />
    <TextRow label="Website" value={cfg.website} onChange={v => set("website", v)} />
    <TextRow label="Primary Brand Color" value={cfg.primary_color} onChange={v => set("primary_color", v)} placeholder="#8B1A1A" />
    <SaveBtn saving={saving} synced={synced} onClick={save} />
  </div>);
};

const Loader = () => <div className="text-center py-4"><div className="w-5 h-5 spinner mx-auto" /></div>;

// ═══════════ DEMO / TEST DATA ═══════════
// One-click seeder + cleaner for the dummy data builds in iter83.
// Wrapped here so non-technical admins can populate a demo workspace
// without touching the API.
const DemoDataConfig = () => {
  const [stats, setStats] = useState(null);
  const [working, setWorking] = useState("");
  const [confirmClear, setConfirmClear] = useState(false);

  const handleSeed = async () => {
    setWorking("seed");
    try {
      const res = await authFetch(`${API}/admin/seed-dummy-data`, { method: "POST" });
      const j = await res.json();
      if (res.ok && j.success) {
        setStats(j.data);
        const created = Object.values(j.data || {}).reduce((a, b) => a + (typeof b === "number" ? b : 0), 0);
        toast.success(
          created === 0 ? "All demo data already present (nothing to add)" : `Seeded ${created} demo records across 9 modules`,
        );
      } else toast.error(j.detail || "Seed failed");
    } catch { toast.error("Seed failed — network error"); }
    finally { setWorking(""); }
  };

  const handleClear = async () => {
    setWorking("clear");
    try {
      const res = await authFetch(`${API}/admin/clear-dummy-data`, { method: "POST" });
      const j = await res.json();
      if (res.ok && j.success) {
        setStats(null);
        const deleted = Object.values(j.data || {}).reduce((a, b) => a + b, 0);
        toast.success(`Removed ${deleted} demo records`);
      } else toast.error(j.detail || "Clear failed");
    } catch { toast.error("Clear failed — network error"); }
    finally { setWorking(""); setConfirmClear(false); }
  };

  return (
    <div className="space-y-3">
      <div className="p-3 rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-200 text-xs text-violet-800 dark:text-violet-300">
        <p className="font-semibold flex items-center gap-1.5 mb-1">
          <Sparkles className="w-3.5 h-3.5" /> What does this do?
        </p>
        <p>
          Seeds <strong>~66 demo records</strong> across 9 modules so you can demo every flow without hand-crafting data:
          12 employee salaries · 10 tasks · 8 expenses · 5 leaves · 12 targets · 5 schemes · 8 collections · 3 grievances · 3 announcements.
          All seeded rows are prefixed <code className="bg-violet-100 dark:bg-violet-900/40 px-1 rounded text-[10px]">DEMO_</code> so you can wipe them in one click later.
        </p>
        <p className="mt-1.5"><strong>Idempotent</strong>: running this multiple times will not create duplicates.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          onClick={handleSeed}
          disabled={!!working}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="admin-seed-dummy-btn"
        >
          {working === "seed" ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <FlaskConical className="w-4 h-4 mr-2" />}
          Seed demo data
        </Button>
        {!confirmClear ? (
          <Button
            variant="outline"
            onClick={() => setConfirmClear(true)}
            disabled={!!working}
            className="border-red-300 text-red-700 hover:bg-red-50"
            data-testid="admin-clear-dummy-btn"
          >
            <Eraser className="w-4 h-4 mr-2" /> Clear demo data
          </Button>
        ) : (
          <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md bg-red-50 border border-red-200">
            <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
            <span className="text-xs text-red-700">Delete all DEMO_ records?</span>
            <Button size="sm" variant="destructive" className="h-6 text-[11px] px-2" onClick={handleClear} disabled={!!working} data-testid="admin-confirm-clear-btn">
              Yes, wipe
            </Button>
            <Button size="sm" variant="outline" className="h-6 text-[11px] px-2" onClick={() => setConfirmClear(false)}>
              Cancel
            </Button>
          </div>
        )}
      </div>

      {stats && (
        <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mt-2" data-testid="seed-stats">
          {Object.entries(stats).map(([k, v]) => (
            <div key={k} className="px-2 py-1.5 rounded bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 text-xs">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">{k.replace(/_/g, " ")}</p>
              <p className="text-sm font-bold font-mono text-slate-900 dark:text-slate-200">{v}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


export default AdminPage;
